// 정답 단어를 눈으로 읽을 수 없는 짧은 코드로 인코딩한다.
// 형식: [자모 인덱스 + 음절 시작 플래그] → XOR 난독화 → 체크섬 1바이트 → Base32
// 외부 라이브러리 없이 순수 JS로만 동작한다.
//
// 자모 인덱스는 0~27이므로 상위 비트가 남는다. 음절의 첫 자모에 0x40 비트를
// 세워 두면 "학교 vs 하꾜" 같은 중의성 없이 정확히 원래 단어로 되돌릴 수 있다.

import { decomposeToJamo, composeCandidates, jamoToIndex, indexToJamo } from './hangul';

// 혼동하기 쉬운 I, O, 0, 1 을 제외한 32자 알파벳
const ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

const XOR_KEY = [0x5b, 0x2e, 0x91, 0xc7, 0x3a, 0x6d, 0xf0, 0x84];

const SYLLABLE_START = 0x40;
const JAMO_MASK = 0x3f;

const toBytes = (word) => {
  const bytes = [];

  for (const char of word) {
    const jamo = decomposeToJamo(char);
    if (!jamo) return null;

    for (let i = 0; i < jamo.length; i++) {
      const index = jamoToIndex(jamo[i]);
      if (index < 0) return null;
      bytes.push(i === 0 ? index | SYLLABLE_START : index);
    }
  }
  return bytes;
};

const checksum = (bytes) => {
  let sum = 7;
  for (let i = 0; i < bytes.length; i++) {
    sum = (sum * 31 + bytes[i]) & 0xff;
  }
  return sum;
};

const obfuscate = (bytes) =>
  bytes.map((b, i) => (b ^ XOR_KEY[i % XOR_KEY.length]) & 0xff);

const deobfuscate = obfuscate; // XOR은 자기 역함수

const toBase32 = (bytes) => {
  let bits = 0;
  let value = 0;
  let out = '';

  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) {
    out += ALPHABET[(value << (5 - bits)) & 31];
  }
  return out;
};

const fromBase32 = (code) => {
  let bits = 0;
  let value = 0;
  const out = [];

  for (const char of code) {
    const index = ALPHABET.indexOf(char);
    if (index < 0) return null;
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return out;
};

/**
 * 한글 단어 → 공유용 시드 코드. 분해 불가능한 단어면 null.
 */
export const encodeSeed = (word) => {
  const bytes = toBytes(word);
  if (!bytes || bytes.length === 0) return null;

  const payload = [...bytes, checksum(bytes)];
  return toBase32(obfuscate(payload));
};

/**
 * 시드 코드 → 한글 단어. 형식이 깨졌거나 체크섬이 맞지 않으면 null.
 */
export const decodeSeed = (code) => {
  if (typeof code !== 'string') return null;

  const normalized = code.trim().toUpperCase();
  if (normalized.length < 2) return null;

  const raw = fromBase32(normalized);
  if (!raw || raw.length < 2) return null;

  const payload = deobfuscate(raw);
  const bytes = payload.slice(0, -1);
  const expected = payload[payload.length - 1];
  if (checksum(bytes) !== expected) return null;

  // 음절 시작 플래그를 따라 자모를 음절 단위로 묶는다
  const syllables = [];
  for (const b of bytes) {
    const jamo = indexToJamo(b & JAMO_MASK);
    if (!jamo) return null;

    if (b & SYLLABLE_START) {
      syllables.push([jamo]);
    } else {
      if (syllables.length === 0) return null;
      syllables[syllables.length - 1].push(jamo);
    }
  }
  if (syllables.length === 0) return null;

  let word = '';
  for (const group of syllables) {
    const candidates = composeCandidates(group);
    // 한 음절로 정확히 떨어지는 조합만 인정한다
    const single = candidates.find((c) => c.length === 1);
    if (!single) return null;
    word += single;
  }

  // 왕복이 일치해야 유효한 시드로 인정한다
  const roundTrip = decomposeToJamo(word);
  if (!roundTrip || roundTrip.join('') !== syllables.flat().join('')) return null;

  return word;
};
